import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@trillion/ui/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@trillion/ui/components/ui/card";
import { passportApi } from "../lib/passport-api";

interface MfaStatus {
  required: boolean;
  recovery_codes_remaining: number;
}

/**
 * Account-page MFA panel. Email two-factor is mandatory + non-disableable (enforced server-side by
 * AUTH_MFA_REQUIRED), so there's no enable/disable toggle — this only manages the break-glass recovery codes:
 * shows how many remain and generates a fresh set (shown ONCE; only hashes are stored server-side).
 */
export function MfaSection() {
  const [status, setStatus] = useState<MfaStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  // Non-null only immediately after a generate — the codes are shown once and never retrievable again.
  const [codes, setCodes] = useState<string[] | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const s = await passportApi.mfa.status();
        if (active) setStatus(s);
      } catch (e) {
        if (active) toast.error(e instanceof Error ? e.message : "Failed to load MFA status");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  async function handleGenerate() {
    setGenerating(true);
    try {
      const result = await passportApi.mfa.generateRecoveryCodes();
      setCodes(result.recovery_codes);
      setStatus((prev) =>
        prev ? { ...prev, recovery_codes_remaining: result.recovery_codes.length } : prev,
      );
      toast.success("New recovery codes generated. Save them now.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to generate recovery codes");
    } finally {
      setGenerating(false);
    }
  }

  function handleCopy() {
    if (!codes) return;
    void navigator.clipboard?.writeText(codes.join("\n")).then(
      () => toast.success("Recovery codes copied."),
      () => toast.error("Copy failed — select and copy manually."),
    );
  }

  if (loading) return null;

  const remaining = status?.recovery_codes_remaining ?? 0;
  const hasCodes = remaining > 0;

  return (
    <Card className="mt-4">
      <CardHeader className="space-y-1">
        <CardTitle className="text-lg font-semibold">Two-factor authentication</CardTitle>
        <CardDescription>
          {status?.required
            ? "Two-factor authentication via email is on for your account."
            : "Recovery codes let you sign in if you can't receive the email code."}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {codes ? (
          <div className="space-y-2">
            <p className="text-sm font-medium">
              Save these recovery codes — they won't be shown again.
            </p>
            <ul className="grid grid-cols-2 gap-1 rounded-md border bg-muted/40 p-3 font-mono text-sm">
              {codes.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
            <Button type="button" variant="outline" size="sm" onClick={handleCopy}>
              Copy codes
            </Button>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            {hasCodes
              ? `You have ${remaining} unused recovery ${remaining === 1 ? "code" : "codes"}.`
              : "You have no recovery codes yet."}
          </p>
        )}
      </CardContent>
      <CardFooter>
        <Button type="button" variant="outline" className="w-full" onClick={handleGenerate} disabled={generating}>
          {generating
            ? "Generating…"
            : hasCodes
              ? "Regenerate recovery codes"
              : "Generate recovery codes"}
        </Button>
      </CardFooter>
    </Card>
  );
}
